import React, { useEffect, useState } from "react";
import { Container, Row, Col, Form, Button } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { Mail, Lock, ArrowRight } from "lucide-react";
import userAPI from "../../api/UserApi";
import { showError, showSuccess } from "../../utils/toastUtils";
import { setAccessToken } from "../../api/axiosInstance";
import useAlphabeticInput from "../../hooks/useAlphabeticInput";
import { handleApiValidationError } from "../../utils/apiErrorHandler";

function LoginCanvas() {
  useAlphabeticInput();
  const navigate = useNavigate();

  const INITIAL_FORM_DATA = {
    email: "",
    password: "",
  };

  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [errors, setErrors] = useState({});

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const isValidEmail = emailRegex.test(formData.email);
  const isValidPassword = formData.password.length >= 6;

  const validate = () => {
    const newErrors = {};
    if (!formData.email) {
      newErrors.email = "Please Enter Email Address";
    } else if (!isValidEmail) {
      newErrors.email = "Please enter a valid email";
    }

    if (!formData.password) {
      newErrors.password = "Enter Password";
    } else if (!isValidPassword) {
      newErrors.password = "Password must be at least 6 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      const res = await userAPI.login(formData);

      if (res.code === 200) {
        localStorage.setItem(
          "token",
          JSON.stringify({
            email: res.data.email,
            username: res.data.username,
          }),
        );

        localStorage.setItem("accessToken", res.data.accessToken);
        navigate("/users/dashboard");
      }
    } catch (err) {
      handleApiValidationError(err, setErrors);
    }
  };

  return (
    <div className="custom-login-wrapper">
      <Container fluid className="p-0">
        <Row className="g-0 min-vh-100">
          {/* Left Side: Editorial Visual */}
          <Col md={6} lg={7} className="d-none d-md-block">
            <div className="custom-login-visual">
              <div className="custom-login-visual-content">
                <span className="visual-tag">LOGIN</span>
                <h3 className="visual-heading">
                  Welcome to <span>Ivoréa</span>
                </h3>
                <p className="visual-desc">
                  Sign in to explore your curated collection and continue
                  your journey with us.
                </p>
              </div>
            </div>
          </Col>

          {/* Right Side: Login Form */}
          <Col
            xs={12}
            md={6}
            lg={5}
            className="d-flex align-items-center justify-content-center"
          >
            <div className="custom-login-container">
              <div className="custom-login-header mb-4">
                <h2 className="custom-login-heading">Welcome Back</h2>
                <p className="custom-login-subheading">
                  Please enter your account details
                </p>
              </div>

              <Form
                method="post"
                className="custom-login-form"
                onSubmit={handleSubmit}
              >
                <Form.Group
                  className="custom-login-field mb-3"
                  controlId="email"
                >
                  <Form.Label className="custom-login-label">
                    Email Address <span className="required-star">*</span>
                  </Form.Label>
                  <div className="custom-login-input-box">
                    <Mail size={18} className="custom-login-icon" />
                    <Form.Control
                      type="email"
                      name="email"
                      placeholder="Email Address"
                      className="custom-login-input"
                      value={formData.email}
                      onChange={handleChange}
                    />
                  </div>
                  {errors?.email && (
                    <span className="custom-input-error">
                      {errors.email}
                    </span>
                  )}
                </Form.Group>

                <Form.Group
                  className="custom-login-field mb-3"
                  controlId="password"
                >
                  <Form.Label className="d-flex justify-content-between align-items-center mb-2">
                    <span className="custom-login-label mb-0">
                      Password <span className="required-star">*</span>
                    </span>
                    <Link
                      to="/users/forgot-password"
                      className="custom-login-link-sm"
                    >
                      Forgot?
                    </Link>
                  </Form.Label>
                  <div className="custom-login-input-box">
                    <Lock size={18} className="custom-login-icon" />
                    <Form.Control
                      type="password"
                      className="custom-login-input"
                      name="password"
                      placeholder="Password"
                      value={formData.password}
                      onChange={handleChange}
                    />
                  </div>
                  {errors?.password && (
                    <span className="custom-input-error">
                      {errors.password}
                    </span>
                  )}
                </Form.Group>

                <Button
                  className="custom-login-submit w-100"
                  type="submit"
                >
                  Login <ArrowRight size={18} className="ms-2" />
                </Button>

                <div className="custom-login-footer mt-4 text-center">
                  <span className="footer-muted-text">
                    Don't have an account?
                  </span>
                  <Link
                    to="/users/register"
                    className="custom-login-link-main ms-2"
                  >
                    Create Account
                  </Link>
                </div>
              </Form>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

export default LoginCanvas;